import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { authService } from '../services/api';
import { myAccessService, companyService, ModulePermission } from '../services/adminApi';
import { getAncestorChain, getModuleForNode } from '../config/moduleTreeUtils';

interface User {
  id: string;
  email: string;
  companyId: string;
  name?: string;
  mustChangePassword?: boolean;
}

interface Company {
  id: string;
  code: string;
  name: string;
}

interface AuthContextValue {
  user: User | null;
  companies: Company[];
  modules: Record<string, ModulePermission>;
  hasAnyAppAccess: boolean;
  unreadNotifications: number;
  isAuthenticated: boolean;
  // True after an admin reset / first login / policy expiry: the app must send
  // the user to /change-password before anything else. The backend enforces the
  // same thing (423 on every feature route), so this only drives navigation.
  mustChangePassword: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ requiresOtp: boolean; userId?: string; maskedEmail?: string; mustChangePassword?: boolean }>;
  verifyLoginOtp: (userId: string, otp: string) => Promise<{ mustChangePassword: boolean }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => void;
  switchCompany: (companyId: string) => Promise<void>;
  hasModuleAccess: (moduleName: string) => boolean;
  hasNodeAccess: (nodeKey: string) => boolean;
  canPerform: (moduleName: string, action: 'add' | 'edit' | 'delete' | 'view') => boolean;
  refreshAccess: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readStoredUser = (): User | null => {
  const raw = localStorage.getItem('user');
  return raw ? JSON.parse(raw) : null;
};

// Every path that receives a token (login, OTP verify, company switch, password
// change) persists it the same way, so mustChangePassword can never drift out of
// sync with the token that encodes it.
const persistSession = (token: string, user: User) => {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
};

// Configurable via REACT_APP_IDLE_TIMEOUT_MINUTES; defaults to 10 minutes.
const IDLE_TIMEOUT_MINUTES = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 10;
const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;
const IDLE_ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
// mousemove/scroll fire rapidly; only re-arm the idle timer at most once per second.
const IDLE_ACTIVITY_THROTTLE_MS = 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(readStoredUser());
  const [companies, setCompanies] = useState<Company[]>([]);
  const [modules, setModules] = useState<Record<string, ModulePermission>>({});
  const [nodePermissions, setNodePermissions] = useState<Record<string, ModulePermission>>({});
  const [hasAnyAppAccess, setHasAnyAppAccess] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [mustChangePassword, setMustChangePassword] = useState(!!readStoredUser()?.mustChangePassword);

  const refreshAccess = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    // Every /admin call is rejected with 423 while a password change is
    // outstanding, so don't fire them — there's nothing to load for a user who
    // can only see the change-password screen.
    if (readStoredUser()?.mustChangePassword) return;
    try {
      const [accessRes, companiesRes] = await Promise.all([
        myAccessService.getMyAccess(),
        companyService.list(),
      ]);
      setModules(accessRes.data.modules || {});
      setNodePermissions(accessRes.data.nodePermissions || {});
      setHasAnyAppAccess(!!accessRes.data.hasAnyAppAccess);
      setUnreadNotifications(accessRes.data.unreadNotifications || 0);
      setCompanies(companiesRes.data.companies || []);
    } catch {
      // access context is best-effort for nav rendering; a failure here
      // shouldn't block the rest of the app
    }
  }, []);

  useEffect(() => {
    if (user) refreshAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared tail of login / verifyLoginOtp / switchCompany.
  const applySession = async (token: string, sessionUser: User, forcedChange: boolean) => {
    persistSession(token, { ...sessionUser, mustChangePassword: forcedChange });
    setUser({ ...sessionUser, mustChangePassword: forcedChange });
    setMustChangePassword(forcedChange);
    await refreshAccess();
  };

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);
    if (response.data.requiresOtp) {
      return { requiresOtp: true, userId: response.data.userId, maskedEmail: response.data.maskedEmail };
    }
    const forcedChange = !!response.data.mustChangePassword;
    await applySession(response.data.token, response.data.user, forcedChange);
    return { requiresOtp: false, mustChangePassword: forcedChange };
  };

  const verifyLoginOtp = async (userId: string, otp: string) => {
    const response = await authService.verifyLoginOtp(userId, otp);
    const forcedChange = !!response.data.mustChangePassword;
    await applySession(response.data.token, response.data.user, forcedChange);
    return { mustChangePassword: forcedChange };
  };

  /**
   * Clears the forced-change gate. The backend returns a replacement token with
   * mustChangePassword dropped — the old one is refused by every feature route,
   * so swapping it in is what actually unblocks the app.
   */
  const changePassword = async (currentPassword: string, newPassword: string) => {
    const response = await authService.changePassword(currentPassword, newPassword);
    const updatedUser = { ...(user as User), mustChangePassword: false };
    if (response.data.token) {
      persistSession(response.data.token, updatedUser);
    } else {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    setUser(updatedUser);
    setMustChangePassword(false);
    await refreshAccess();
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setModules({});
    setNodePermissions({});
    setCompanies([]);
    setMustChangePassword(false);
  };

  // Auto-logout after IDLE_TIMEOUT_MS of no user interaction (security requirement — see ADM-03).
  // AuthProvider renders outside BrowserRouter, so a full navigation is used for the redirect.
  useEffect(() => {
    if (!user) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    let lastReset = 0;
    const handleIdleTimeout = () => {
      logout();
      window.location.href = '/login';
    };
    const resetTimer = () => {
      const now = Date.now();
      if (now - lastReset < IDLE_ACTIVITY_THROTTLE_MS) return;
      lastReset = now;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleIdleTimeout, IDLE_TIMEOUT_MS);
    };
    // If another tab clears the session (idle-logout or manual logout there),
    // log this tab out too so we don't leave an authenticated view open.
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'token' && !e.newValue) {
        logout();
        window.location.href = '/login';
      }
    };

    resetTimer();
    IDLE_ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer));
    window.addEventListener('storage', handleStorage);

    return () => {
      clearTimeout(timeoutId);
      IDLE_ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
      window.removeEventListener('storage', handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const switchCompany = async (companyId: string) => {
    const response = await authService.switchCompany(companyId);
    await applySession(response.data.token, response.data.user, !!response.data.mustChangePassword);
  };

  const hasModuleAccess = (moduleName: string) => {
    if (!hasAnyAppAccess) return true; // legacy/bootstrap users — see backend loadAccessContext
    const perm = modules[moduleName];
    return !!perm && perm.accessType !== 'NO ACCESS' && perm.canView;
  };

  // ADM-07d: mirrors backend permissions.ts's resolveNodePermission — checks
  // `nodeKey` then each ancestor (closest first) for an explicit override
  // before falling back to the module-level check above. With no per-node
  // overrides saved yet (ADM-07c), this resolves identically to
  // hasModuleAccess for every node.
  const hasNodeAccess = (nodeKey: string) => {
    if (!hasAnyAppAccess) return true;
    for (const ancestorKey of getAncestorChain(nodeKey)) {
      const perm = nodePermissions[ancestorKey];
      if (perm) return perm.accessType !== 'NO ACCESS' && perm.canView;
    }
    const moduleKey = getModuleForNode(nodeKey) ?? nodeKey;
    return hasModuleAccess(moduleKey);
  };

  const canPerform = (moduleName: string, action: 'add' | 'edit' | 'delete' | 'view') => {
    if (!hasAnyAppAccess) return true;
    const perm = modules[moduleName];
    if (!perm || perm.accessType === 'NO ACCESS') return false;
    return action === 'add' ? perm.canAdd : action === 'edit' ? perm.canEdit : action === 'delete' ? perm.canDelete : perm.canView;
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      companies,
      modules,
      hasAnyAppAccess,
      unreadNotifications,
      isAuthenticated: !!user,
      mustChangePassword,
      login,
      verifyLoginOtp,
      changePassword,
      logout,
      switchCompany,
      hasModuleAccess,
      hasNodeAccess,
      canPerform,
      refreshAccess,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, companies, modules, nodePermissions, hasAnyAppAccess, unreadNotifications, mustChangePassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
